import useAccount from "@/modules/account/hooks/useAccount";
import { ContractorPanelVisitService } from "../services/contractorPanelVisitService";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { useCallback, useMemo } from "react";
import { InfiniteData } from "@tanstack/react-query";
import { useCachedInfiniteQuery } from "@/hooks/useCachedQuery";
import { SuccessFetchListResponse } from "@/services/api";
import { useTranslation } from "react-i18next";
import { Module } from "@/constants";
import {
  MobileList,
  MobileListItem,
  MobileListItemHeader,
  MobileListItemContent,
  MobileListItemField,
  MobileListItemBadge,
} from "@/components/mobile-list";
import { PATH_CONTRACTOR_VISITS_EDIT } from "@/constants";
import { generatePath } from "react-router-dom";
import { ContractorPanelVisitsFilter } from "./ContractorPanelVisitsFilter";
import { TodayVisitsFilter } from "./TodayVisitsFilter";
import { isPlannedDurationVisible } from "../utils/isPlannedDurationVisible";
import { usePlannedDurationVisibilityClock } from "../hooks/usePlannedDurationVisibilityClock";
import { Button } from "@/components/ui/button";
import { GeolocationStatusContainer } from "./GeolocationStatus";
import { GeolocationAlert } from "./GeolocationAlert";
import { GeolocationProvider } from "@/contexts/GeolocationContext";
import { getStatusBadgeVariant } from "../utils/getStatusBadgeVariant";
import { ContractorPanelVisitsHeader } from "./ContractorPanelVisitsHeader";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { useContractorPanelVisitFilters } from "../hooks/useContractorPanelVisitFilters";
import {
  convertFiltersToBackend,
  convertSortingToBackend,
} from "../utils/convertFilters";

export const ContractorPanelVisitsDirectory = () => {
  const { t } = useTranslation();

  const { formatDateTime, formatDuration, calculateRealDuration } =
    useFormatters();
  const { account } = useAccount();

  const {
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    handleToggleTodayFilter,
  } = useContractorPanelVisitFilters();

  const queryKey = useMemo(
    () => [Module.ContractorPanelVisits, account?.contractor_id, filters],
    [account?.contractor_id, filters],
  );

  const query = useCachedInfiniteQuery<
    SuccessFetchListResponse<ReadContractorVisit>,
    Error,
    InfiniteData<SuccessFetchListResponse<ReadContractorVisit>>,
    typeof queryKey,
    string | undefined
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const backendFilters = convertFiltersToBackend(filters);
      const backendSorting = convertSortingToBackend(filters);

      return ContractorPanelVisitService.getList({
        lastItem: pageParam,
        filters: backendFilters,
        sorting: backendSorting,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.last_item,
    enabled: !!account?.contractor_id,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const visibilityExpiry = query.data?.pages
    .flatMap((page) => page.items)
    .reduce<string | undefined>((earliest, visit) => {
      const expiry = visit.planned_duration_visibility_valid_until;
      return expiry && (!earliest || Date.parse(expiry) < Date.parse(earliest))
        ? expiry
        : earliest;
    }, undefined);
  const visibilityNow = usePlannedDurationVisibilityClock(visibilityExpiry);

  const renderEmptyState = () => {
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="text-gray-500 text-base mb-4">
            {t(`contractorVisits.emptyState.noResults`)}
          </div>
          <Button
            onClick={clearFilters}
            variant="outline"
            className="h-10 px-6"
          >
            {t(`contractorVisits.emptyState.clearFilters`)}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex justify-center py-8">
        <div className="text-center text-gray-500">
          <div className="text-base">
            {t(`contractorVisits.emptyState.noVisits`)}
          </div>
        </div>
      </div>
    );
  };

  const renderItem = useCallback(
    (visit: ReadContractorVisit) => {
      return (
        <MobileListItem
          key={visit.id}
          to={generatePath(PATH_CONTRACTOR_VISITS_EDIT, {
            contractorVisitId: visit.id,
          })}
        >
          <MobileListItemHeader
            title={visit.project_name}
            subtitle={visit.store_name}
            badge={
              <MobileListItemBadge
                variant={getStatusBadgeVariant(visit.status)}
              >
                {t(`contractorVisitsStatuses.${visit.status}`)}
              </MobileListItemBadge>
            }
          />
          <MobileListItemContent>
            <MobileListItemField
              label={t(`contractorVisits.fields.date`)}
              value={visit.date}
            />
            {visit.hours_editable_by_contractor ? (
              <>
                {visit.start_datetime && (
                  <MobileListItemField
                    label={t("contractorVisits.fields.start_datetime")}
                    value={formatDateTime(visit.start_datetime)}
                  />
                )}
                {visit.end_datetime && (
                  <MobileListItemField
                    label={t("contractorVisits.fields.end_datetime")}
                    value={formatDateTime(visit.end_datetime)}
                  />
                )}
              </>
            ) : (
              <>
                {visit.started_at && (
                  <MobileListItemField
                    label={t("contractorVisits.fields.started_at")}
                    value={formatDateTime(visit.started_at)}
                  />
                )}
                {visit.ended_at && (
                  <MobileListItemField
                    label={t("contractorVisits.fields.ended_at")}
                    value={formatDateTime(visit.ended_at)}
                  />
                )}
              </>
            )}
            <MobileListItemField
              label={t(`contractorVisits.fields.project_role_name`)}
              value={visit.project_role_name}
            />
            {isPlannedDurationVisible(visit, visibilityNow) &&
              visit.planned_duration && (
                <MobileListItemField
                  label={t(`contractorVisits.fields.planned_duration`)}
                  value={formatDuration(visit.planned_duration)}
                />
              )}
            {visit.planned_duration_visible &&
              (visit.confirmable_by_contractor ||
                visit.hours_editable_by_contractor) &&
              visit.real_duration && (
                <MobileListItemField
                  label={t(`contractorVisits.fields.real_duration`)}
                  value={calculateRealDuration(
                    visit.start_datetime,
                    visit.end_datetime,
                  )}
                />
              )}
          </MobileListItemContent>
        </MobileListItem>
      );
    },
    [formatDateTime, formatDuration, calculateRealDuration, t, visibilityNow],
  );

  return (
    <GeolocationProvider>
      <div className="p-4 pb-20">
        <ContractorPanelVisitsHeader
          title={t(`contractorVisits.title.my`)}
          rightElement={
            <TodayVisitsFilter
              isActive={filters.isTodayAndUpcoming ?? false}
              onToggle={handleToggleTodayFilter}
            />
          }
        />

        <GeolocationStatusContainer />

        <GeolocationAlert />
        <MobileList
          query={query}
          renderItem={renderItem}
          className="space-y-3"
          emptyComponent={renderEmptyState()}
        />

        <ContractorPanelVisitsFilter
          filters={filters}
          onApply={setFilters}
          onClear={clearFilters}
        />
      </div>
    </GeolocationProvider>
  );
};
